import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Shield, UserPlus, Calendar, CheckCircle, Users, Star } from "lucide-react";

const HowItWorksSection = () => {
  const steps = [
    {
      number: "01",
      title: "CREATE YOUR VERIFIED ACCOUNT",
      icon: UserPlus,
      items: [
        { label: "Identity Verification", desc: "Complete our secure ID check process for age verification and community safety" },
        { label: "Profile Creation", desc: "Set up your gaming preferences, skill levels, and availability" },
        { label: "Community Access", desc: "Join a network of verified players and hosts in your community" },
      ]
    },
    {
      number: "02",
      title: "DISCOVER OR HOST GAMING EVENTS",
      icon: Calendar,
      items: [
        { label: "Browse Events", desc: "Search upcoming games and tournaments by game type, location, and date" },
        { label: "Host Your Own", desc: "Use our intuitive event creation tools to organize games at your home or a partnered venue" },
        { label: "Customize Everything", desc: "Set participation rules, capacity limits, entry fees, and house rules" },
      ]
    },
    {
      number: "03",
      title: "PLAY AND CONNECT",
      icon: Users,
      items: [
        { label: "Attend Events", desc: "RSVP to games that match your interests and schedule" },
        { label: "Build Your Network", desc: "Connect with regular playing partners and rivals" },
        { label: "Track Your Stats", desc: "Monitor your tournament performance and gaming history" },
      ]
    },
  ];

  const safetyFeatures = [
    { icon: Shield, label: "ID Verification Required", desc: "Every subscriber completes identity and age verification" },
    { icon: CheckCircle, label: "Clear Event Rules", desc: "Hosts must disclose all participation requirements upfront" },
    { icon: Star, label: "Review System", desc: "Rate and review events and players to maintain community standards" },
  ];

  return (
    <div data-testid="how-it-works-section">
      {/* Section Header */}
      <div className="mb-12">
        <h2 className="font-bebas text-4xl md:text-5xl tracking-wider text-white">
          WELCOME TO BOARD2BOSTON™
        </h2>
        <p className="mt-4 text-[#A1A1AA] font-ubuntu text-lg max-w-3xl">
          Board2Boston™ is a premier platform connecting card and board game enthusiasts across the country and in your community. Whether you're a competitive Spades player, a Whist aficionado, or enjoy classic board games, B2B™ makes it easy to find games, host tournaments, and connect with fellow players in a safe, verified community.
        </p>
      </div>

      {/* How It Works Steps */}
      <div className="mb-16">
        <h3 className="font-bebas text-2xl tracking-wider text-[#7a6200] mb-8">
          HOW BOARD2BOSTON™ WORKS
        </h3>
        <p className="text-[#A1A1AA] font-ubuntu mb-8">
          Our platform operates on a simple three-step process designed to get you playing quickly and safely.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {steps.map((step) => (
            <Card key={step.number} className="bg-[#0A0A0A] border border-white/5 hover:border-[#7a6200]/30 transition-colors">
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-6">
                  <span className="font-bebas text-5xl text-[#7a6200]/30">{step.number}</span>
                  <div className="w-12 h-12 rounded-full bg-[#7a6200]/20 flex items-center justify-center">
                    <step.icon className="w-6 h-6 text-[#7a6200]" />
                  </div>
                </div>
                <h4 className="font-bebas text-xl tracking-wider text-white mb-4">
                  {step.title}
                </h4>
                <ul className="space-y-4">
                  {step.items.map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <CheckCircle className="w-5 h-5 text-[#7a6200] mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-white font-ubuntu font-medium">{item.label}</p>
                        <p className="text-[#A1A1AA] font-ubuntu text-sm">{item.desc}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Safety Section */}
      <div className="bg-[#0A0A0A] border border-white/5 rounded-lg p-8">
        <h3 className="font-bebas text-2xl tracking-wider text-[#7a6200] mb-4">
          SAFETY AND SECURITY
        </h3>
        <p className="text-[#A1A1AA] font-ubuntu mb-8">
          Board2Boston™ prioritizes player safety through comprehensive verification and transparent event hosting.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {safetyFeatures.map((feature, i) => (
            <div key={i} className="flex gap-4">
              <div className="w-10 h-10 rounded-full bg-[#7a6200]/20 flex items-center justify-center flex-shrink-0">
                <feature.icon className="w-5 h-5 text-[#7a6200]" />
              </div>
              <div>
                <p className="text-white font-ubuntu font-medium">{feature.label}</p>
                <p className="text-[#52525B] font-ubuntu text-sm">{feature.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HowItWorksSection;
